// Client Portal — RAG/Knowledge-Base config, theme, and localStorage-backed
// helpers (conversations, analytics, API keys, audit, prompt config).
// Extracted from App.js; the prerequisite lib for the RAG components.
import { safeCssColor } from "./utils";

export const parseMessageText = (text) => {
  const urlPattern = /(https?:\/\/[^\s<>"')\]]+)/g;
  urlPattern.lastIndex = 0;
  const segments = text.split(urlPattern);
  return segments.map((seg, i) =>
    /^https?:\/\//.test(seg)
      ? { type: "link", value: seg, key: i }
      : { type: "text", value: seg, key: i },
  );
};

export const getUserInitials = (user) => {
  if (!user) return "U";
  const f = user.first_name?.[0] || "";
  const l = user.last_name?.[0] || "";
  return (f + l).toUpperCase() || "U";
};

export const DEFAULT_RAG_COLORS = {
  bg: "#0d1b2e",
  sidebar: "#152338",
  chatBg: "#1e3352",
  accent: "#4a6fa5",
  text: "#8dafd8",
  headings: "#e8eef4",
  userBubble: "#243d63",
  botBubble: "#1e3352",
};

export const THEME_PRESETS = [
  {
    name: "Midnight",
    colors: {
      bg: "#0d1b2e",
      sidebar: "#152338",
      chatBg: "#1e3352",
      accent: "#4a6fa5",
      text: "#8dafd8",
      headings: "#e8eef4",
      userBubble: "#243d63",
      botBubble: "#1e3352",
    },
  },
  {
    name: "Daylight",
    colors: {
      bg: "#f0f2f5",
      sidebar: "#e4e8ec",
      chatBg: "#ffffff",
      accent: "#5a7ea8",
      text: "#4a5568",
      headings: "#1a2a3c",
      userBubble: "#5a7ea8",
      botBubble: "#e4e8ec",
    },
  },
  {
    name: "Forest",
    colors: {
      bg: "#0f1e14",
      sidebar: "#162a1c",
      chatBg: "#1d3625",
      accent: "#4a9a5e",
      text: "#8dc8a0",
      headings: "#e4f2e8",
      userBubble: "#2d7a3f",
      botBubble: "#1d3625",
    },
  },
  {
    name: "Ember",
    colors: {
      bg: "#1e0f0f",
      sidebar: "#2a1616",
      chatBg: "#361d1d",
      accent: "#b05a4a",
      text: "#d8a08d",
      headings: "#f4e8e4",
      userBubble: "#a04030",
      botBubble: "#361d1d",
    },
  },
  {
    name: "Slate",
    colors: {
      bg: "#1a1a2e",
      sidebar: "#22223a",
      chatBg: "#2c2c48",
      accent: "#7a6fa5",
      text: "#afa5d8",
      headings: "#eee8f4",
      userBubble: "#6a5fa0",
      botBubble: "#2c2c48",
    },
  },
];

export const DEFAULT_QUICK_PROMPTS = {
  General: [
    "What documents do you have available?",
    "Summarize the key topics in this knowledge base",
    "What are the most recent updates?",
  ],
  "Deep Dive": [
    "Explain the main policies in detail",
    "What are the compliance requirements?",
    "Show me related documents on this topic",
  ],
};

export const getRagConfig = (facilityId) => {
  try {
    const raw = JSON.parse(
      localStorage.getItem(`hyve-rag-config-${facilityId}`) || "{}",
    );
    const config = {};
    if (raw.colors && typeof raw.colors === "object") {
      config.colors = {};
      for (const [k, v] of Object.entries(raw.colors)) {
        if (typeof v === "string") config.colors[k] = safeCssColor(v);
      }
    }
    if (
      raw.topics &&
      typeof raw.topics === "object" &&
      !Array.isArray(raw.topics)
    ) {
      config.topics = {};
      for (const [cat, prompts] of Object.entries(raw.topics)) {
        if (typeof cat === "string" && Array.isArray(prompts)) {
          config.topics[cat] = prompts.filter((p) => typeof p === "string");
        }
      }
    }
    // Layout / branding fields
    const safeUrl = (val) => {
      if (typeof val !== "string" || !val) return "";
      try {
        return new URL(val).href;
      } catch {
        return "";
      }
    };
    const safeInt = (val, min, max, fallback) => {
      const n = parseInt(val, 10);
      return Number.isFinite(n) && n >= min && n <= max ? n : fallback;
    };
    if (raw.logoUrl) config.logoUrl = safeUrl(raw.logoUrl);
    if (raw.botAvatarUrl) config.botAvatarUrl = safeUrl(raw.botAvatarUrl);
    if (raw.widgetWidthPct)
      config.widgetWidthPct = safeInt(raw.widgetWidthPct, 1, 100, 100);
    if (raw.widgetHeightPct)
      config.widgetHeightPct = safeInt(raw.widgetHeightPct, 1, 100, 70);
    if (raw.borderRadius)
      config.borderRadius = safeInt(raw.borderRadius, 0, 32, 12);
    if (raw.fontSize) config.fontSize = safeInt(raw.fontSize, 10, 20, 13);
    if (typeof raw.showSidebar === "boolean")
      config.showSidebar = raw.showSidebar;
    if (typeof raw.requireLogin === "boolean")
      config.requireLogin = raw.requireLogin;
    const MATCH_MODES = new Set(["off", "keyword", "semantic"]);
    const sanitizeMatch = (m) => {
      if (!m || typeof m !== "object") return { mode: "off" };
      const mode = MATCH_MODES.has(m.mode) ? m.mode : "off";
      let threshold = Number(m.threshold);
      if (!Number.isFinite(threshold) || threshold < 0.5 || threshold > 0.99)
        threshold = 0.78;
      return {
        mode,
        pattern: typeof m.pattern === "string" ? m.pattern : "",
        description: typeof m.description === "string" ? m.description : "",
        threshold,
        syncedRuleId: typeof m.syncedRuleId === "string" ? m.syncedRuleId : "",
        syncedVectorId:
          typeof m.syncedVectorId === "string" ? m.syncedVectorId : "",
      };
    };
    if (Array.isArray(raw.chatImages)) {
      config.chatImages = raw.chatImages
        .filter(
          (img) =>
            img &&
            typeof img === "object" &&
            typeof img.url === "string" &&
            img.url,
        )
        .map((img) => ({
          id:
            typeof img.id === "string" && img.id
              ? img.id
              : `img_${Math.random().toString(36).slice(2, 10)}`,
          label: typeof img.label === "string" ? img.label : "",
          url: img.url,
          addedAt: typeof img.addedAt === "string" ? img.addedAt : "",
          match: sanitizeMatch(img.match),
        }));
    }
    return config;
  } catch {
    return {};
  }
};

export const setRagConfig = (facilityId, config) => {
  localStorage.setItem(`hyve-rag-config-${facilityId}`, JSON.stringify(config));
};

// Resize an image File to fit within maxWidth x maxHeight, preserving aspect
// ratio. SVG (vector) and GIF (animation) are returned unchanged. PNG keeps
// transparency; other types are re-encoded as JPEG at the given quality. If
// the source is already smaller than the cap, the original file is returned.
export const resizeImageToFile = (file, opts = {}) => {
  const { maxWidth = 720, maxHeight = 1280, quality = 0.85 } = opts;
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }
    if (file.type === "image/svg+xml" || file.type === "image/gif") {
      resolve(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) {
        URL.revokeObjectURL(url);
        resolve(file);
        return;
      }
      const ratio = Math.min(maxWidth / w, maxHeight / h, 1);
      if (ratio >= 1) {
        URL.revokeObjectURL(url);
        resolve(file);
        return;
      }
      const targetW = Math.max(1, Math.round(w * ratio));
      const targetH = Math.max(1, Math.round(h * ratio));
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, targetW, targetH);
      URL.revokeObjectURL(url);
      const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
      const ext = outType === "image/png" ? ".png" : ".jpg";
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const base = (file.name || "image").replace(/\.[^.]+$/, "");
          resolve(new File([blob], `${base}${ext}`, { type: outType }));
        },
        outType,
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
};

// ═══════════════════════════════════════════════════════════════════
// CONVERSATION PERSISTENCE
// ═══════════════════════════════════════════════════════════════════

export const CONV_STORAGE_KEY = (fid, email) =>
  `hyve-rag-chats-${fid}-${email}`;
export const MAX_CONVERSATIONS = 100;

export const loadConversations = (facilityId, email) => {
  try {
    const raw = JSON.parse(
      localStorage.getItem(CONV_STORAGE_KEY(facilityId, email)) || "[]",
    );
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(
        (c) =>
          c &&
          typeof c.id === "string" &&
          Array.isArray(c.messages) &&
          c.createdAt,
      )
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  } catch {
    return [];
  }
};

export const saveConversations = (facilityId, email, conversations) => {
  const trimmed = conversations.slice(0, MAX_CONVERSATIONS);
  localStorage.setItem(
    CONV_STORAGE_KEY(facilityId, email),
    JSON.stringify(trimmed),
  );
};

export const createConversation = (facilityId, email, welcomeMsg) => {
  const conv = {
    id: `conv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: "New conversation",
    messages: welcomeMsg ? [welcomeMsg] : [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const all = loadConversations(facilityId, email);
  all.unshift(conv);
  saveConversations(facilityId, email, all);
  return conv;
};

export const updateConversation = (facilityId, email, convId, updates) => {
  const all = loadConversations(facilityId, email);
  const idx = all.findIndex((c) => c.id === convId);
  if (idx === -1) return all;
  all[idx] = { ...all[idx], ...updates, updatedAt: new Date().toISOString() };
  saveConversations(facilityId, email, all);
  return all;
};

export const deleteConversation = (facilityId, email, convId) => {
  const all = loadConversations(facilityId, email).filter(
    (c) => c.id !== convId,
  );
  saveConversations(facilityId, email, all);
  return all;
};

export const autoTitle = (messages) => {
  const first = messages.find((m) => m.role === "user");
  if (!first) return "New conversation";
  const t = first.content.slice(0, 60);
  return t.length < first.content.length ? t + "…" : t;
};

// ── Analytics event tracking ──────────────────────────────────────

export const ANALYTICS_KEY = (fid) => `hyve-rag-analytics-${fid}`;

export const trackEvent = (facilityId, eventType, data = {}) => {
  try {
    const key = ANALYTICS_KEY(facilityId);
    const events = JSON.parse(localStorage.getItem(key) || "[]");
    events.push({
      type: eventType,
      timestamp: new Date().toISOString(),
      ...data,
    });
    // Keep last 5000 events max
    if (events.length > 5000) events.splice(0, events.length - 5000);
    localStorage.setItem(key, JSON.stringify(events));
  } catch {
    /* storage full — ignore */
  }
};

export const loadAnalytics = (facilityId) => {
  try {
    return JSON.parse(localStorage.getItem(ANALYTICS_KEY(facilityId)) || "[]");
  } catch {
    return [];
  }
};

// ── API Key persistence ───────────────────────────────────────────

export const APIKEYS_KEY = (fid) => `hyve-rag-apikeys-${fid}`;

export const loadApiKeys = (facilityId) => {
  try {
    const raw = JSON.parse(
      localStorage.getItem(APIKEYS_KEY(facilityId)) || "[]",
    );
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

export const saveApiKeys = (facilityId, keys) => {
  localStorage.setItem(APIKEYS_KEY(facilityId), JSON.stringify(keys));
};

export const generateApiKey = () => {
  const seg = () =>
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10);
  return `hyve_${seg()}_${seg()}`;
};

// ── Audit log persistence ─────────────────────────────────────────

export const AUDIT_KEY = (fid) => `hyve-rag-audit-${fid}`;
export const MAX_AUDIT_EVENTS = 5000;

export const logAudit = (facilityId, action, userEmail, details = {}) => {
  try {
    const key = AUDIT_KEY(facilityId);
    const events = JSON.parse(localStorage.getItem(key) || "[]");
    events.push({
      id: `aud-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      action,
      user: userEmail,
      timestamp: new Date().toISOString(),
      ...details,
    });
    if (events.length > MAX_AUDIT_EVENTS)
      events.splice(0, events.length - MAX_AUDIT_EVENTS);
    localStorage.setItem(key, JSON.stringify(events));
  } catch {
    /* storage full */
  }
};

export const loadAuditLog = (facilityId) => {
  try {
    const raw = JSON.parse(localStorage.getItem(AUDIT_KEY(facilityId)) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

// ── Prompt config persistence ─────────────────────────────────────

export const PROMPT_CONFIG_KEY = (fid) => `hyve-rag-prompt-config-${fid}`;

export const DEFAULT_PROMPT_CONFIG = {
  systemPrompt:
    "You are a helpful knowledge base assistant. Answer questions based on the provided documents. If you don't know the answer, say so honestly. Always cite your sources.",
  topK: 5,
  similarityThreshold: 0.7,
  reranking: false,
  tone: "professional",
  maxResponseLength: "medium",
  citationStyle: "inline",
  temperature: 0.3,
};

export const loadPromptConfig = (facilityId) => {
  try {
    const raw = JSON.parse(
      localStorage.getItem(PROMPT_CONFIG_KEY(facilityId)) || "{}",
    );
    return { ...DEFAULT_PROMPT_CONFIG, ...raw };
  } catch {
    return { ...DEFAULT_PROMPT_CONFIG };
  }
};

export const savePromptConfig = (facilityId, config) => {
  localStorage.setItem(PROMPT_CONFIG_KEY(facilityId), JSON.stringify(config));
};

// ═══════════════════════════════════════════════════════════════════
// AUTH HELPERS
// ═══════════════════════════════════════════════════════════════════

// Auth/fetch helpers (getToken, authFetch, emailIntelFetch, authUpload) live in ./lib/api.

// ═══════════════════════════════════════════════════════════════════
// ARTICLE GENERATOR PORTAL
// ═══════════════════════════════════════════════════════════════════
